import { useCallback, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { v4 as uuidv4 } from "uuid";
import {
  useRecoverUserNamePost,
  type RecoverUserNameRequestBodyBody,
  type RecoverUserNameResponse,
} from "@fmc-digital/auth-api-generated";
import { config } from "@/config";
import { useRecoverUsername } from "@/contexts/RecoverUsernameContext";
import {
  clearRecoverUsernameHeaders,
  createRecaptchaGenerationFailedError,
  createRecaptchaNotInitializedError,
  setRecoverUsernameHeaders,
  type RecoverUsernameFormValues,
} from "@/utils/recover-username.utils";
import { trackForgotEmailEvent, ForgotEmailEvent } from "@/utils/analytics.utils";

export type OnSuccessExtra = (
  response: RecoverUserNameResponse,
  values: RecoverUsernameFormValues,
) => void;

export function useRecoverUsernameSubmit(onSuccessExtra?: OnSuccessExtra) {
  const [responseBodyError, setResponseBodyError] = useState<unknown>(null);
  const { setResponse, setLastName } = useRecoverUsername();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { mutate: recoverUsername, isPending: isLoading, error } = useRecoverUserNamePost();

  const onSubmit = useCallback(
    async (values: RecoverUsernameFormValues) => {
      // Track forgot email clicked
      trackForgotEmailEvent(ForgotEmailEvent.CLICKED);
      
      setResponseBodyError(null);

      const requestBody: RecoverUserNameRequestBodyBody = {
        lname: values.lastName,
        ssn: values.ssn.replace(/\D/g, ""),
      };

      if (!executeRecaptcha) {
        console.error("reCAPTCHA provider not initialized");
        setResponseBodyError(createRecaptchaNotInitializedError());
        return;
      }

      let captchaToken: string | undefined;
      try {
        captchaToken = await executeRecaptcha("recover_username");
        if (!captchaToken || captchaToken.trim() === "") {
          throw new Error("reCAPTCHA token generation returned empty");
        }
      } catch (err) {
        console.error("Failed to execute reCAPTCHA:", err);
        setResponseBodyError(createRecaptchaGenerationFailedError(err));
        return;
      }

      const guid = uuidv4();
      setRecoverUsernameHeaders(captchaToken, guid);

      recoverUsername(
        { data: requestBody },
        {
          onSuccess: (response) => {
            if (response && "errorDetails" in response && response.errorDetails) {
              console.error("Recover username error in response body:", response);
              setResponseBodyError({
                data: { errorDetails: response.errorDetails },
                response: { data: { errorDetails: response.errorDetails } },
              });
              clearRecoverUsernameHeaders();
              return;
            }

            setResponseBodyError(null);

            if (response) {
              // Track the successful validation event
              trackForgotEmailEvent(ForgotEmailEvent.DATA_VALIDATION_SUCCESS);
              
              setResponse(response);
              setLastName(values.lastName.trim());
              onSuccessExtra?.(response, values);
              if (response.guid) {
                config.setEnv("recoverUsernameGuid", response.guid);
              }
            }
            clearRecoverUsernameHeaders();
          },
          onError: (err) => {
            console.error("Recover username error:", err);
            setResponseBodyError(null);
            clearRecoverUsernameHeaders();
          },
        },
      );
    },
    [executeRecaptcha, recoverUsername, setResponse, setLastName, onSuccessExtra],
  );

  return { onSubmit, isLoading, error, responseBodyError };
}
